import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { t } from "@/i18n";
import { Card, Spinner, EmptyState } from "@/components/ui";

type PublicMedia = {
  id: string;
  kind: "audio" | "video" | "image" | "text_block" | "link";
  storagePath: string | null;
  url: string | null;
  textContent: string | null;
  sortOrder: number;
};

type PublicAssignment = {
  id: string;
  title: string;
  instructions: string;
  languageCode: string;
  status: string;
  type: { code: string; baseContentKind: string; name: string; description: string; classroomLabel: string };
  media: PublicMedia[];
};

const src = (m: PublicMedia) => m.url || (m.storagePath ? `/uploads/${m.storagePath}` : "");

export const Route = createFileRoute("/s/$id")({
  component: StudentAssignmentPage,
});

function StudentAssignmentPage() {
  const { id } = Route.useParams();
  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["public-assignment", id],
    queryFn: async () => {
      const res = await fetch(`/api/public/assignments/${id}`);
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as PublicAssignment;
    },
  });

  if (isLoading) return <Spinner label={t("app.loading")} />;
  if (isError || !a) {
    return (
      <Shell>
        <EmptyState>{t("student.notFound")}</EmptyState>
      </Shell>
    );
  }

  const audio = a.media.filter((m) => m.kind === "audio");
  const images = a.media.filter((m) => m.kind === "image");
  const videos = a.media.filter((m) => m.kind === "video");
  const texts = a.media.filter((m) => m.kind === "text_block");
  const links = a.media.filter((m) => m.kind === "link");

  return (
    <Shell>
      <div className="space-y-6">
        <header className="space-y-4">
          <Masthead label="MIS DEBERES" />
          <h1 className="text-center text-3xl font-bold">{a.title}</h1>
          {a.instructions && (
            <p className="whitespace-pre-line text-muted-foreground">{a.instructions}</p>
          )}
        </header>

        {a.type.description && (
          <p className="whitespace-pre-line rounded-lg bg-muted/40 p-3 text-sm">
            {a.type.description}
          </p>
        )}

        {audio.map((m, i) => (
          <Card key={m.id} className="p-4">
            <p className="mb-2 text-sm font-medium">
              🎧 {audio.length > 1 ? `${t("student.part")} ${i + 1}` : t("student.listen")}
            </p>
            <audio controls src={src(m)} className="w-full" />
          </Card>
        ))}

        {images.map((m) => (
          <Card key={m.id} className="p-4">
            <img src={src(m)} alt="" className="max-h-[32rem] w-full rounded-md object-contain" />
          </Card>
        ))}

        {videos.map((m) => (
          <Card key={m.id} className="p-4">
            <video controls src={src(m)} className="w-full rounded-md" />
          </Card>
        ))}

        {texts.map((m) => (
          <Card key={m.id} className="p-5">
            <p className="whitespace-pre-line text-lg leading-relaxed">{m.textContent}</p>
          </Card>
        ))}

        {links.map((m) => (
          <Card key={m.id} className="p-4">
            <a
              href={m.url ?? "#"}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              🔗 {t("student.openActivity")}
            </a>
          </Card>
        ))}

        {a.media.length === 0 && <EmptyState>{t("student.noContent")}</EmptyState>}

        <footer className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
          ✍️ {t("student.answerInClassroom")}
        </footer>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30">
      <main className="mx-auto max-w-3xl px-6 py-8">{children}</main>
    </div>
  );
}

// Editorial masthead — each letter sits in an alternating black/white box,
// like a newspaper nameplate (Bodoni Moda). Spaces become gaps between boxes.
function Masthead({ label }: { label: string }) {
  let letterIdx = 0;
  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      {label.split("").map((ch, i) => {
        if (ch === " ") return <span key={i} className="inline-block w-3" />;
        const dark = letterIdx % 2 === 0;
        letterIdx += 1;
        return (
          <span
            key={i}
            className={`font-serif-display flex h-12 w-9 items-center justify-center text-3xl font-bold sm:h-16 sm:w-12 sm:text-5xl ${
              dark ? "bg-black text-white" : "bg-white text-black ring-1 ring-black"
            }`}
          >
            {ch}
          </span>
        );
      })}
    </div>
  );
}